import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { SocialIcons } from "../../common/components/SocialIcons";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/contact.scss";
import { useI18n } from "../../i18n/I18nProvider";

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);

    return { ref, inView };
}

const CONTACT_INFO = {
    name: "Centrum Cyberbezpieczeństwa WSIZ ",
    address: "ul. mjr. Henryka Sucharskiego 2 ",
    postalCity: "35-225 Rzeszów",
    room: "RA107",
    email: "cc@wsiz.edu.pl",
    phone: "17 866 14 40",
};

const ContactPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();

    const infoHook = useInView(0.15);
    const refInfo = infoHook.ref as React.RefObject<HTMLDivElement>;
    const inViewInfo = infoHook.inView;

    const socialsHook = useInView(0.15);
    const refSocials = socialsHook.ref as React.RefObject<HTMLDivElement>;
    const inViewSocials = socialsHook.inView;

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);


    const handleMouseMoveGlobal = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("contact-page", { "contact-page--ready": ready })}
            onMouseMove={handleMouseMoveGlobal}
            aria-label={t("contact_section_label")}
        >

            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />


            <MatrixHero
                title={t("contact_hero_title")}
                subtitle={t("contact_hero_subtitle")}
                density={0.5}
            />

            <div className="contact-page__inner">

                <div
                    ref={refInfo}
                    className={classNames("contact-card", { "contact-card--visible": inViewInfo })}
                >
                    <h2 className="contact-card__org">{t("contact_org_name")}</h2>

                    <div className="contact-card__details">


                        <div className="contact-detail">
                            <div className="contact-detail__body">
                                <span className="contact-detail__label">{t("contact_label_location")}</span>
                                <address className="contact-detail__value highlight">
                                    {CONTACT_INFO.address}<br />
                                    {CONTACT_INFO.postalCity}
                                </address>
                            </div>
                        </div>


                        <div className="contact-detail">
                            <div className="contact-detail__body">
                                <span className="contact-detail__label">{t("contact_label_room")}</span>
                                <span className="contact-detail__value highlight">{CONTACT_INFO.room}</span>
                            </div>
                        </div>


                        <div className="contact-detail">
                            <div className="contact-detail__body">
                                <span className="contact-detail__label">{t("contact_label_email")}</span>
                                <a href={`mailto:${CONTACT_INFO.email}`} className="contact-detail__link">
                                    {CONTACT_INFO.email}
                                </a>
                            </div>
                        </div>


                        <div className="contact-detail">
                            <div className="contact-detail__body">
                                <span className="contact-detail__label">{t("contact_label_phone")}</span>
                                <a href={`tel:+48${CONTACT_INFO.phone.replace(/\s/g, "")}`} className="contact-detail__link">
                                    {CONTACT_INFO.phone}
                                </a>
                            </div>
                        </div>

                    </div>
                </div>


                <div
                    ref={refSocials}
                    className={classNames("contact-socials", { "contact-socials--visible": inViewSocials })}
                >
                    <h3 className="contact-socials__title">{t("contact_socials_title")}</h3>
                    <SocialIcons className="social-icons--large mt-4" />
                </div>

            </div>
        </section>
    );
};

export default ContactPage;